/**
 * CampaignDurationField — shared "Czas trwania kampanii" date-range field
 * composition (label + `DateRangeButtonField` + translated error), used by
 * `DraftDefaultsSection` (full `/offers/new` form) and `QuickAddOfferPicker`'s
 * inline `creating`-mode form (calendar-driven quick-add, CU-86cb8grgr).
 *
 * Presentational only — callers own the `useController`/RHF wiring and pass
 * already-resolved `fromValue`/`toValue`/`onChange`, since the two forms bind
 * to different `useForm<T>` value shapes and field names.
 */
import { differenceInCalendarDays } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { DateRangeButtonField } from '@ooh/ui/components/date-range-button-field';
import { FormField } from '@ooh/ui/components/form-field';
import { InlineErrorMessage } from '@ooh/ui/components/inline-error-message';
import { formatDateValue, parseOptionalDateInputValue } from '@ooh/ui/lib/date-input-helpers';

/** Advisory-only threshold: most media don't allow reservations shorter than 2
 *  weeks, so a shorter offer period is usually unintentional (non-blocking). */
const SHORT_PERIOD_THRESHOLD_DAYS = 14;

/** Inclusive day count (e.g. same start/end date is a 1-day period). Returns
 * `null` when either date is missing or the range is invalid (end before start). */
function getInclusiveDurationDays(
  fromValue: Date | null | undefined,
  toValue: Date | null | undefined,
): number | null {
  if (!(fromValue instanceof Date) || !(toValue instanceof Date)) return null;
  const days = differenceInCalendarDays(toValue, fromValue) + 1;
  return days >= 1 ? days : null;
}

export interface CampaignDurationFieldProps {
  /** Element id + the `DateRangeButtonField`'s default `data-testid` (kept stable across callers). */
  id: string;
  /** `FormField`'s wrapper testid — kept distinct from `id` so both elements resolve uniquely in tests. */
  fieldTestId: string;
  /** `DateRangeButtonField`'s `data-testid`. Defaults to `id` when omitted (existing callers). */
  testId?: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  fromValue: Date | null | undefined;
  toValue: Date | null | undefined;
  onChange: (next: { from: Date | null; to: Date | null }) => void;
  error?: string;
}

export function CampaignDurationField({
  id,
  fieldTestId,
  testId,
  label,
  placeholder,
  disabled,
  fromValue,
  toValue,
  onChange,
  error,
}: CampaignDurationFieldProps) {
  const { t } = useTranslation('common');
  const durationDays = getInclusiveDurationDays(fromValue, toValue);
  const isShortPeriod = durationDays !== null && durationDays < SHORT_PERIOD_THRESHOLD_DAYS;

  return (
    <FormField label={label} htmlFor={id} testId={fieldTestId} error={error}>
      <DateRangeButtonField
        id={id}
        testId={testId ?? id}
        disabled={disabled}
        placeholder={placeholder}
        locale={pl}
        fromValue={formatDateValue(fromValue)}
        toValue={formatDateValue(toValue)}
        onChange={({ from, to }) =>
          onChange({
            from: parseOptionalDateInputValue(from) ?? null,
            to: parseOptionalDateInputValue(to) ?? null,
          })
        }
      />
      {isShortPeriod ? (
        <InlineErrorMessage tone="warning" testId={`${fieldTestId}-warning`}>
          {t('campaignDurationField.shortPeriodWarning')}
        </InlineErrorMessage>
      ) : null}
    </FormField>
  );
}
